/* eslint-disable max-lines */
import InventoryIcon from '@mui/icons-material/Inventory';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import BatchShippingIcon, { batchShippingTooltip } from 'common/components/BatchShippingIcon';
import TextField from 'common/forms/TextField';
import { filter, find, first, isEmpty, map } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidV4 } from 'uuid';

import DateField from 'common/forms/DateField';
import SelectField from 'common/forms/SelectField';
import TimeField from 'common/forms/TimeField';
import { validateTimeNotInFuture } from 'common/forms/formValidations';
import useRoles from 'common/hooks/useRoles';
import perfectRxTheme from 'common/theme/perfectRxTheme';
import { EnableShippingService } from 'common/utils';
import { format } from 'date-fns';
import { shipmentMode } from 'enums/common.enum';
import { useNavigate } from 'react-router-dom';
import { listShippingOptions, updateBatchShipmentFilter } from 'store/thunks/batchShipmentThunk';
import { createOrderShipment } from 'store/thunks/patientThunks';
import { getFulfillingPharmacies } from 'store/thunks/pharmacyThunks';

const ShipmentModeWatcher = ({ form, shippingOptions, currentMode }) => {
  const savedOptionsRef = useRef({});
  const prevModeRef = useRef(currentMode);

  useEffect(() => {
    const prevMode = prevModeRef.current;
    if (prevMode === currentMode) return;

    const currentOption = form.getState().values.shipmentType;
    savedOptionsRef.current[prevMode] = currentOption;
    prevModeRef.current = currentMode;

    if (savedOptionsRef.current[currentMode]) {
      const availableOptions =
        currentMode === shipmentMode.PRX_SHIPMENT
          ? filter(shippingOptions, (opt) => opt.carrierCode.toLowerCase().includes('fedex'))
          : shippingOptions;
      const saved = savedOptionsRef.current[currentMode];
      const isSavedValid = availableOptions.some((opt) => opt.optionCode === saved);
      if (isSavedValid) {
        form.change('shipmentType', saved);
        return;
      }
    }

    const availableOptions =
      currentMode === shipmentMode.PRX_SHIPMENT
        ? filter(shippingOptions, (opt) => opt.carrierCode.toLowerCase().includes('fedex'))
        : shippingOptions;

    const currentOption2 = form.getState().values.shipmentType;
    const isCurrentValid = availableOptions.some((opt) => opt.optionCode === currentOption2);
    if (!isCurrentValid && availableOptions.length > 0) {
      const defaultOption = find(availableOptions, { isDefault: true }) || availableOptions[0];
      form.change('shipmentType', defaultOption.optionCode);
    }
  }, [currentMode, form, shippingOptions]);

  return null;
};

ShipmentModeWatcher.propTypes = {
  form: PropTypes.shape({
    getState: PropTypes.func.isRequired,
    change: PropTypes.func.isRequired,
  }).isRequired,
  shippingOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  currentMode: PropTypes.string.isRequired,
};

const SendToShippingButton = ({ mpi, orderId, orderStatus, onSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const hasAccess = useRoles();
  const orders = useSelector(({ patient }) => patient[mpi]?.orders);
  const order = find(orders, (orderObj) => orderObj.orderId === orderId);
  const lineItems = order?.lineItems ?? [];
  const { currentTenantId } = useSelector(({ auth }) => auth);
  const fulfillingPharmacies = useSelector(({ pharmacy }) => pharmacy[currentTenantId] ?? []);
  const defaultShipmentMode =
    find(fulfillingPharmacies, { fulfillingPharmacyId: order?.fulfillingPharmacyId })
      ?.shipmentConfig?.shipmentRoutes?.routeTo ?? shipmentMode.SHIP_VIA_SHIPSTATION;

  const lineItemsInBatchShipment = filter(lineItems, { inBatchShipmentQueue: true });
  const lineItemsNotInBatchShipment = filter(lineItems, {
    inBatchShipmentQueue: false,
    lineItemStatus: 'PRODUCT_FULFILLED',
    processingState: 'READY_FOR_SHIPMENT',
  });

  const showExternalShipment = !isEmpty(lineItemsNotInBatchShipment);

  const lineItemsNotInBatchShipmentIds = map(lineItemsNotInBatchShipment, 'lineItemId');
  const isDisabled = !lineItemsNotInBatchShipmentIds.length;
  const listObject = [
    {
      listData: {
        heading: 'In Batch Shipment',
        value: !isEmpty(lineItemsInBatchShipment)
          ? lineItemsInBatchShipment
          : [{ default: 'Nothing found...' }],
      },
    },
    { listData: { heading: 'In Order Shipment', value: lineItemsNotInBatchShipment } },
  ];
  const { shippingOptions } = useSelector(({ systemConfigs }) => systemConfigs);

  const defaultShippingOption = first(lineItems)?.shippingOption;

  const handleCloseModal = () => {
    setShowModal(false);
  };
  const handleOpenModal = () => setShowModal(true);

  const handleSendToShipment = ({
    shipmentType,
    shipmentMode: selectedShipmentMode,
    carrierCode,
    trackingNumber,
    externalShipmentId,
    shipDate,
    shipTime,
  }) => {
    setIsLoading(true);

    const shipDateStr = shipDate ? format(new Date(shipDate), 'yyyy-MM-dd') : null;
    const shipTimeStr = shipTime || null;

    dispatch(
      createOrderShipment({
        mpi,
        orderId,
        shipmentType,
        shipmentMode: selectedShipmentMode,
        lineItemIds: lineItemsNotInBatchShipmentIds,
        ...(selectedShipmentMode === shipmentMode.EXTERNAL_SHIPMENT && {
          carrierCode,
          externalShipmentId: externalShipmentId ?? uuidV4(),
          trackingNumber,
        }),
        shipDate: shipDateStr,
        shipTime: shipTimeStr,
      })
    ).then((success) => {
      if (success) {
        enqueueSnackbar('Successfully send to shipment', { variant: 'success' });
        onSuccess?.();
      }

      setIsLoading(false);
      handleCloseModal();
    });
  };

  const navigateToBatchShipmentQueue = (rxOrderId) => {
    dispatch(
      updateBatchShipmentFilter({
        filter: { orderId: rxOrderId },
      })
    );
    navigate('/batchShipments');
  };

  useEffect(() => {
    if (isEmpty(shippingOptions)) {
      dispatch(listShippingOptions());
    }
  }, [shippingOptions, dispatch]);

  useEffect(() => {
    if (isEmpty(fulfillingPharmacies)) {
      dispatch(getFulfillingPharmacies());
    }
  }, [fulfillingPharmacies, dispatch]);

  if (!order) return null;

  return (
    <>
      <Dialog
        open={showModal}
        onClose={handleCloseModal}
        aria-describedby='SendToShipping-Dialog-header'
        fullWidth
      >
        <DialogTitle id='SendToShipping-Dialog-header'>Send to Shipping</DialogTitle>

        <Form
          onSubmit={handleSendToShipment}
          initialValues={{
            shipmentType: defaultShippingOption,
            carrierCode: 'UPS',
            shipmentMode: defaultShipmentMode,
          }}
          render={({ handleSubmit, values, form }) => (
            <form noValidate onSubmit={handleSubmit}>
              <ShipmentModeWatcher
                form={form}
                shippingOptions={shippingOptions}
                currentMode={values.shipmentMode}
              />
              <DialogContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    {isDisabled ? (
                      <Typography fontWeight='bold'>
                        All line items are in the batch shipment queue. Complete the batch shipment
                        or remove items from the batch queue to ship separately
                      </Typography>
                    ) : (
                      <>
                        <List
                          sx={{
                            width: '100%',
                            position: 'relative',
                            overflow: 'auto',
                            maxHeight: 300,
                            '& ul': { padding: 0 },
                          }}
                          subheader={<li />}
                        >
                          {map(listObject, (listItem) => (
                            <li key={`section-${listItem.listData.heading}`}>
                              <ul>
                                <ListSubheader sx={{ fontWeight: 'bold' }}>
                                  <Typography sx={{ fontWeight: 'bold' }}>
                                    {listItem.listData.heading}
                                  </Typography>
                                </ListSubheader>

                                {map(listItem.listData.value, (lineItem) => (
                                  <li key={`section-${lineItem.lineItemId}`}>
                                    <ul>
                                      <ListItem key={`item-${lineItem.rxNumber}`}>
                                        <ListItemIcon>
                                          {lineItem.inBatchShipmentQueue && (
                                            <Tooltip
                                              title={batchShippingTooltip({
                                                isBusinessAddress: lineItem.isBusinessAddress,
                                                isClickable: true,
                                              })}
                                              arrow
                                              placement='top'
                                            >
                                              <BatchShippingIcon
                                                onClick={() =>
                                                  navigateToBatchShipmentQueue(orderId)
                                                }
                                                color={perfectRxTheme.palette.warning.warningColour}
                                                showBadge={lineItem.isBusinessAddress}
                                              />
                                            </Tooltip>
                                          )}

                                          {!lineItem.inBatchShipmentQueue && !lineItem.default && (
                                            <InventoryIcon color='primary' />
                                          )}
                                        </ListItemIcon>

                                        <ListItemText
                                          primary={lineItem?.itemName ?? lineItem.default}
                                          secondary={lineItem?.rxNumber}
                                        />
                                      </ListItem>
                                    </ul>
                                  </li>
                                ))}
                              </ul>
                            </li>
                          ))}
                        </List>
                        <Typography sx={{ fontWeight: 'bold' }}>
                          The items under Order Shipment will be sent in this shipment. Click
                          Confirm to proceed
                        </Typography>
                      </>
                    )}
                  </Grid>

                  {showExternalShipment && (
                    <Grid item xs={12}>
                      <SelectField
                        id='SendToShipping-shippingService-dropdown'
                        label='Shipping Service'
                        name='shipmentMode'
                        disabled={isDisabled}
                        lexicographicSort={false}
                      >
                        <MenuItem value={shipmentMode.SHIP_VIA_SHIPSTATION}>
                          Ship Via ShipStation
                        </MenuItem>
                        {EnableShippingService && (
                          <MenuItem value={shipmentMode.PRX_SHIPMENT}>Send to Shipping</MenuItem>
                        )}
                        <MenuItem value={shipmentMode.EXTERNAL_SHIPMENT}>
                          Enter Shipment Manually
                        </MenuItem>
                      </SelectField>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <SelectField
                      id='SendToShipping-shippingType-dropdown'
                      label='Select Shipping Method'
                      name='shipmentType'
                      disabled={isDisabled}
                    >
                      {map(
                        values.shipmentMode === shipmentMode.PRX_SHIPMENT
                          ? filter(shippingOptions, (option) =>
                              option.carrierCode.toLowerCase().includes('fedex')
                            )
                          : shippingOptions,
                        ({ shippingOption, optionCode }, key) => {
                          return (
                            <MenuItem value={optionCode} key={key}>
                              {shippingOption}
                            </MenuItem>
                          );
                        }
                      )}
                    </SelectField>
                  </Grid>

                  {values.shipmentMode === shipmentMode.EXTERNAL_SHIPMENT && (
                    <Grid item xs={12}>
                      <SelectField
                        id='Shipment-Manually-dropdown'
                        label='Carrier Code'
                        name='carrierCode'
                        disabled
                      >
                        <MenuItem value='UPS'>UPS</MenuItem>
                      </SelectField>

                      <TextField label='Tracking Number' name='trackingNumber' required />

                      <DateField
                        label='Shipping Date'
                        name='shipDate'
                        id='BatchShipmentsTable-shipDate-dateField'
                        required
                        maxDate={new Date()}
                      />

                      <TimeField
                        label='Shipping Time (24-hour)'
                        name='shipTime'
                        id='BatchShipmentsTable-shipTime-timeField'
                        required
                        helperText='Use 24-hour format, e.g., 14:30'
                        validations={[
                          validateTimeNotInFuture('Time cannot be in the future', 'shipDate'),
                        ]}
                      />

                      <TextField
                        label='External Shipment ID (If Available)'
                        name='externalShipmentId'
                      />
                    </Grid>
                  )}
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button variant='outlined' color='secondary' onClick={handleCloseModal}>
                  Cancel
                </Button>
                <LoadingButton
                  loading={isLoading}
                  variant='contained'
                  type='submit'
                  disabled={isDisabled}
                >
                  Send
                </LoadingButton>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>

      {hasAccess.createOrderShipment && (
        <Grid justifyContent='flex-end'>
          <Tooltip
            title={orderStatus === 'HOLD' ? 'Order payment in hold' : ''}
            placement='top'
            arrow
          >
            <span>
              <LoadingButton
                variant='contained'
                onClick={handleOpenModal}
                disabled={orderStatus === 'HOLD'}
                sx={{
                  px: 1,
                  fontSize: '0.75rem',
                  minHeight: '30px',
                  lineHeight: 'normal',
                }}
              >
                Send to Shipping
              </LoadingButton>
            </span>
          </Tooltip>
        </Grid>
      )}
    </>
  );
};

SendToShippingButton.propTypes = {
  orderId: PropTypes.string.isRequired,
  mpi: PropTypes.string.isRequired,
  orderStatus: PropTypes.string.isRequired,
  onSuccess: PropTypes.func,
};

SendToShippingButton.defaultProps = {
  onSuccess: undefined,
};

export default SendToShippingButton;
